import '../styles/home.css'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'

import { copyText } from '../components/Utilities'
import { getFlagFallback, getFlagUrl } from '../utils/countryFlags'
import { getAreas, getIndicators, getCountries } from '../actions/searchActions'
import LandingGlobalHeader from '../components/landing/LandingGlobalHeader'
import LandingFooter from '../components/landing/LandingFooter'
import LandingIcon from '../components/landing/LandingIcon'
import useLandingAuth from '../components/landing/useLandingAuth'

const AREA_CARDS = [
  {
    id: 6,
    title: 'Economic Activity & Employment',
    description: 'GDP, production, labor markets, and headline activity signals.',
    image: 'https://i.imgur.com/a774rVA.png',
    icon: '/static/archive.svg',
  },
  {
    id: 5,
    title: 'Prices & Wages',
    description: 'Inflation, core measures, wage dynamics, and relative price signals.',
    image: 'https://i.imgur.com/qi29Y70.png',
    icon: '/static/shopping-cart.svg',
  },
  {
    id: 3,
    title: 'Fiscal Accounts & Public Debt',
    description: 'Revenue, expenditure, balances, financing, and debt composition.',
    image: 'https://i.imgur.com/glUa8Q3.png',
    icon: '/static/activity.svg',
  },
  {
    id: 2,
    title: 'External Accounts',
    description: 'Current account, reserves, remittances, trade, and external funding.',
    image: 'https://i.imgur.com/0IaRwmH.png',
    icon: '/static/truck.svg',
  },
  {
    id: 4,
    title: 'Money & Banking',
    description: 'Monetary aggregates, deposits, credit, and banking conditions.',
    image: 'https://i.imgur.com/HCGa8EP.png',
    icon: '/static/dollar-sign.svg',
  },
  {
    id: 1,
    title: 'Financial Markets',
    description: 'Interest rates, sovereign spreads, exchange rates, and market sentiment.',
    image: 'https://i.imgur.com/rI3DSQf.png',
    icon: '/static/trending-up.svg',
  },
]

const normalizeAreaName = (value = '') =>
  value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const ACCOUNT_FEATURES = [
  {
    title: 'Custom dashboards',
    description: 'Save and organize recurring country-indicator views for regular monitoring.',
    icon: 'grid',
  },
  {
    title: 'Reusable collections',
    description: 'Keep your preferred indicator combinations ready for future sessions.',
    icon: 'folderTree',
  },
  {
    title: 'Faster workflows',
    description: 'Move from exploration to download with less repetitive setup work.',
    icon: 'bolt',
  },
]

const PLATFORM_ITEMS = [
  {
    title: 'Traceable and documented series',
    description: 'Definitions, sources, frequencies, and units remain visible all the way from search to download.',
    icon: 'compass',
  },
  {
    title: 'Cross-country comparison by design',
    description: 'Search by country and indicator combinations to build a comparable regional view in a few clicks.',
    icon: 'globe',
  },
  {
    title: 'Interactive charts and downloads',
    description: 'Adjust frequency, inspect metadata, and export the indicators you need for policy work and research.',
    icon: 'chartColumn',
  },
]

const SHOW_COUNTRY_BRIEFINGS_SECTION = false

const LANDING_BRIEFINGS = [
  {
    country: 'Argentina',
    flag: 'Argentina',
    period: 'Q1 2026',
    pdfUrl: 'https://d3h48y1385wws8.cloudfront.net/briefings/Argentina/2026/q1/Argentina-2026-q1.pdf',
    isPublished: false,
    summary: 'Argentina briefings will provide a concise view of recent macroeconomic conditions, policy signals, and the main indicators to monitor in LMW.',
  },
  {
    country: 'Brazil',
    flag: 'Brazil',
    summary: 'Brazil briefings will provide a concise view of recent macroeconomic conditions, policy signals, and the main indicators to monitor in LMW.',
  },
  {
    country: 'Mexico',
    flag: 'Mexico',
    summary: 'Mexico briefings will provide a concise view of recent macroeconomic conditions, policy signals, and the main indicators to monitor in LMW.',
  },
]

const FALLBACK_COUNTRIES = [
  'Argentina',
  'Bahamas',
  'Barbados',
  'Belize',
  'Bolivia',
  'Brazil',
  'Chile',
  'Colombia',
  'Costa Rica',
  'Dominican Republic',
  'Ecuador',
  'El Salvador',
  'Guatemala',
  'Guyana',
  'Haiti',
  'Honduras',
  'Jamaica',
  'Mexico',
  'Nicaragua',
  'Panama',
  'Paraguay',
  'Peru',
  'Suriname',
  'Trinidad and Tobago',
  'Uruguay',
  'Venezuela',
]

const API_CODE_SAMPLE = [
  { key: '_id', value: '88', type: 'number' },
  { key: 'lmwuuid', value: '101010100220', type: 'number' },
  { key: 'Country', value: '"Argentina"', type: 'string' },
  { key: 'Frequency', value: '"Annual"', type: 'string' },
  { key: 'Year', value: '2017', type: 'number' },
  { key: 'Date', value: '"2017-12-31"', type: 'string' },
  { key: 'Value', value: '15.5386303428514', type: 'number' },
  { key: 'Indicator', value: '"GDP (Current Prices)"', type: 'string' },
  { key: 'Unit', value: '"millions of USD"', type: 'string' },
  { key: 'Transformation', value: '"YoY %"', type: 'string' },
  { key: 'Area', value: '"01_Economic Activity and Employment"', type: 'string' },
  { key: 'Topic', value: '"01_National Accounts"', type: 'string' },
  {
    key: 'Definition',
    value: `"Gross Domestic Product (GDP) at current prices
    represents the total monetary value of all final
    goods and services produced within a country's
    borders during a given period. It serves as a key
    indicator of overall economic activity."`,
    type: 'string',
  },
  { key: 'Source', value: '"INDEC"', type: 'string' },
  { key: 'Notes', value: 'null', type: 'null' },
  { key: 'Updated', value: '"2026-03-13"', type: 'string' },
]

const ABOUT_VIDEO_URL = 'https://d3h48y1385wws8.cloudfront.net/videos/LatinMacroWatchENGSUBSENG.mp4'
const ABOUT_VIDEO_POSTER = 'https://i.imgur.com/floLmVo.jpeg'
const ABOUT_VIDEO_POSTER_FALLBACK = '/static/home-header.png'
const LANDING_INDICATOR_COUNT = 180

const LANDING_SELECT_STYLES = {
  menuPortal: base => ({ ...base, zIndex: 120 }),
}

function MainScreen() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loadingUser, userInfo, openModal, logoutHandler, authModals } = useLandingAuth()

  const [areaValue, setAreaValue] = useState({ value: null, label: 'All Categories' })
  const [indicatorValue, setIndicatorValue] = useState(null)
  const [aboutVideoPoster, setAboutVideoPoster] = useState(ABOUT_VIDEO_POSTER)
  const [quickSearchPulse, setQuickSearchPulse] = useState(false)

  const areaInfo = useSelector(state => state.areaList)
  const { areaList } = areaInfo || {}

  const indicatorInfo = useSelector(state => state.indicatorList)
  const { indicatorList } = indicatorInfo || {}

  const countryInfo = useSelector(state => state.countryList)
  const { countryList } = countryInfo || {}

  useEffect(() => {
    dispatch(getAreas())
    dispatch(getIndicators())
    dispatch(getCountries())
  }, [dispatch])

  useEffect(() => {
    const posterImage = new Image()
    posterImage.src = ABOUT_VIDEO_POSTER
    posterImage.onload = () => setAboutVideoPoster(ABOUT_VIDEO_POSTER)
    posterImage.onerror = () => setAboutVideoPoster(ABOUT_VIDEO_POSTER_FALLBACK)
  }, [])

  useEffect(() => {
    const nodes = document.querySelectorAll('.landing-reveal')
    if (!nodes.length) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        })
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' }
    )

    nodes.forEach(node => observer.observe(node))

    return () => observer.disconnect()
  }, [])

  const indicatorFiltered = useMemo(() => {
    const list = Array.isArray(indicatorList) ? indicatorList : []
    const filtered = areaValue.value
      ? list.filter(item => item.foreign_area === areaValue.value)
      : list

    return [...filtered].sort((a, b) => a.name.localeCompare(b.name))
  }, [areaValue.value, indicatorList])

  const indicatorOptions = useMemo(() => {
    return indicatorFiltered.map(item => ({
      value: item.id,
      label: item.name,
      area: item.foreign_area,
      topic: item.foreign_topic,
    }))
  }, [indicatorFiltered])

  const featuredCountries = useMemo(() => {
    const countries = Array.isArray(countryList) ? [...countryList] : []
    const countryNames = new Set(countries.map(country => country.name))
    const missingFallbackCountries = FALLBACK_COUNTRIES
      .filter(name => !countryNames.has(name))
      .map(name => ({ name }))

    if (countries.length > 0) {
      return [...countries, ...missingFallbackCountries].sort((a, b) => a.name.localeCompare(b.name))
    }

    return FALLBACK_COUNTRIES.map(name => ({ name }))
  }, [countryList])

  const changeCategoryHandler = (item) => {
    if (!item || !item.id) {
      setAreaValue({ value: null, label: 'All Categories' })
      setIndicatorValue(null)
      return
    }

    setAreaValue({ value: item.id, label: item.beauty_name })
    setIndicatorValue(null)
  }

  const changeIndicatorHandler = (option) => {
    setIndicatorValue(option)

    if (!option) return
    if (option.area !== areaValue.value) {
      const areaItem = (areaList || []).find(item => item.id === option.area)
      if (areaItem) {
        setAreaValue({ value: areaItem.id, label: areaItem.beauty_name })
      }
    }
  }

  const searchHandler = () => {
    if (!indicatorValue) {
      navigate('/search')
      return
    }

    navigate(
      `/search?indicator=${encodeURIComponent(indicatorValue.label)}&indicatorId=${encodeURIComponent(indicatorValue.value)}&topic=${encodeURIComponent(indicatorValue.topic)}`
    )
  }

  const enterHandler = (event) => {
    if (event.key === 'Enter') {
      searchHandler()
    }
  }

  const scrollToSection = (id) => {
    const section = document.getElementById(id)
    if (!section) return
    const top = section.getBoundingClientRect().top + window.scrollY - 88
    window.scrollTo({ top, behavior: 'smooth' })
  }

  const areaSearchHandler = (id) => {
    const areaCard = AREA_CARDS.find(item => item.id === id)
    const areaItem = (areaList || []).find(item => {
      const beautyName = normalizeAreaName(item.beauty_name || '')
      const areaName = normalizeAreaName(areaCard?.title || '')
      return item.id === id || beautyName === areaName || beautyName.includes(areaName) || areaName.includes(beautyName)
    })

    if (areaItem) {
      changeCategoryHandler(areaItem)
      setQuickSearchPulse(false)
      window.requestAnimationFrame(() => {
        setQuickSearchPulse(true)
      })
      window.setTimeout(() => {
        setQuickSearchPulse(false)
      }, 1800)
    }

    scrollToSection('landing-quick-search')
  }

  return (
    <div className='landing-page-shell'>
      <header className='landing-hero' id='landing-home'>
        <div className='landing-hero-overlay'></div>

        <div className='landing-topbar'>
          <div className='landing-topbar-brand'>
            <img src='/static/idb-white.svg' className='landing-idb-logo' alt='Inter-American Development Bank' />
          </div>
        </div>

        <div className='landing-hero-content landing-hero-content-main container'>
          <div className='landing-hero-copy landing-hero-copy-main landing-reveal is-visible'>
            <h1 className='landing-hero-title'>LATIN MACRO WATCH</h1>
            <p className='landing-hero-subtitle'>The Macroeconomic Compass for Latin America and the Caribbean</p>

            <div className='landing-hero-cta-row'>
              <button className='landing-hero-primary' onClick={searchHandler}>Explore Dataset</button>
            </div>

            <div className={`landing-hero-searchbar ${quickSearchPulse ? 'landing-search-panel-pulse' : ''}`} id='landing-quick-search'>
              <select
                className='landing-hero-category-select'
                value={areaValue.value || ''}
                onChange={(event) => {
                  const nextId = Number(event.target.value)
                  const nextArea = (areaList || []).find(item => item.id === nextId)
                  changeCategoryHandler(nextArea || { id: null })
                }}
                aria-label='Select category'
              >
                <option value=''>All Categories</option>
                {(areaList || []).map(item => (
                  <option key={item.id} value={item.id}>{item.beauty_name}</option>
                ))}
              </select>

              <div className='landing-hero-indicator-select'>
                <Select
                  className='home-select'
                  classNamePrefix='home-select'
                  options={indicatorOptions}
                  placeholder={indicatorList && indicatorList.length > 0 ? `Search above ${LANDING_INDICATOR_COUNT} indicators…` : 'Search indicators…'}
                  value={indicatorValue}
                  onChange={changeIndicatorHandler}
                  onKeyDown={enterHandler}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition='fixed'
                  styles={LANDING_SELECT_STYLES}
                />
              </div>

              <button className='landing-hero-search-button' onClick={searchHandler} aria-label='Search indicator'>
                <LandingIcon name='search' size={19} />
              </button>
            </div>
          </div>
        </div>

      </header>

      <LandingGlobalHeader
        loadingUser={loadingUser}
        userInfo={userInfo}
        onOpenModal={openModal}
        onLogout={logoutHandler}
        compactActions
      />

      <main>
        <section className='landing-story-section' id='about-lmw'>
          <div className='container landing-story-grid'>
            <div className='landing-story-copy landing-reveal is-visible'>
              <h2 className='landing-section-title'>What is Latin Macro Watch</h2>
              <p className='landing-section-description'>
                Welcome to Latin Macro Watch (LMW), a comprehensive macroeconomic database developed by the Inter-American Development Bank (IDB). LMW delivers up-to-date indicators covering a broad range of macroeconomic variables across all IDB borrowing member countries in Latin America and the Caribbean.
              </p>
            </div>

            <div className='landing-story-video'>
              <div className='landing-story-video-frame'>
                <video
                  className='landing-story-video-player'
                  controls
                  preload='metadata'
                  playsInline
                  poster={aboutVideoPoster}
                >
                  <source src={ABOUT_VIDEO_URL} type='video/mp4' />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </section>

        <section className='landing-coverage-section' id='coverage-scale'>
          <div className='container'>
            <div className='landing-section-header centered landing-reveal'>
              <h2 className='landing-section-title'>Regional Coverage &amp; Scale</h2>
              <p className='landing-section-description'>Comprehensive toolkit for advanced macro-financial analysis.</p>
            </div>

            <div className='landing-stat-grid'>
              <div className='landing-stat-card landing-reveal' style={{ '--landing-delay': '40ms' }}>
                <strong>{countryList?.length ?? 26}</strong>
                <span>Countries</span>
                <p>All IDB borrowing members in Latin America and the Caribbean.</p>
              </div>
              <div className='landing-stat-card landing-reveal' style={{ '--landing-delay': '120ms' }}>
                <strong>180+</strong>
                <span>Indicators</span>
                <p>Standardized macroeconomic indicators with consistent metadata.</p>
              </div>
              <div className='landing-stat-card landing-reveal' style={{ '--landing-delay': '200ms' }}>
                <strong>35+</strong>
                <span>Years</span>
                <p>Of historical data validated from official sources.</p>
              </div>
            </div>

            <div className='landing-featured-label'>Featured Economies</div>
            <div className='landing-country-cloud'>
              {featuredCountries.map((country) => (
                <div className='landing-country-chip' key={country.name}>
                  <span className='landing-country-flag-wrap'>
                    <img
                      src={getFlagUrl(country.name, 40)}
                      alt={`${country.name} flag`}
                      onError={(event) => {
                        event.currentTarget.style.display = 'none'
                        event.currentTarget.nextSibling.style.display = 'inline-flex'
                      }}
                    />
                    <span className='landing-country-flag-fallback' style={{ display: 'none' }}>{getFlagFallback(country.name)}</span>
                  </span>
                  <span className='landing-country-name'>{country.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className='landing-platform-section'>
          <div className='container landing-insights-grid'>
            <div className='landing-insights-feature landing-reveal'>
              <h2 className='landing-section-title'>Built to move from exploration to evidence quickly.</h2>
              <p className='landing-section-description'>The landing keeps the practical workflows of the current product while presenting them with a clearer editorial rhythm and stronger product hierarchy.</p>
            </div>
            <div className='landing-insight-list'>
              {PLATFORM_ITEMS.map((item, index) => (
                <div className='landing-insight-card landing-reveal' style={{ '--landing-delay': `${index * 110}ms` }} key={item.title}>
                  <div className='landing-insight-icon'><LandingIcon name={item.icon} /></div>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className='landing-areas-section' id='landing-areas'>
          <div className='container'>
            <div className='landing-section-header centered landing-reveal'>
              <h2 className='landing-section-title'>Six Areas of Coverage</h2>
              <p className='landing-section-description'>Six core macroeconomic pillars, structured in line with how official institutions organize economic accounts.</p>
            </div>

            <div className='landing-area-grid'>
              {AREA_CARDS.map((area, index) => (
                <button key={area.id} className='landing-area-card landing-reveal' type='button' onClick={() => areaSearchHandler(area.id)} style={{ '--landing-delay': `${index * 80}ms` }}>
                  <div className='landing-area-icon landing-area-icon-image'>
                    <img src={area.icon} alt='' aria-hidden='true' />
                  </div>
                  <div className='landing-area-card-body'>
                    <h3>{area.title}</h3>
                    <p>{area.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {SHOW_COUNTRY_BRIEFINGS_SECTION && (
          <section className='landing-briefings-section' id='landing-briefings'>
            <div className='container'>
              <div className='landing-section-header centered landing-reveal'>
                <h2 className='landing-section-title'>Country Briefings</h2>
                <p className='landing-section-description'>Upcoming country summaries of recent macroeconomic developments for LMW-covered economies.</p>
              </div>

              <div className='landing-briefings-grid'>
                {LANDING_BRIEFINGS.map((briefing, index) => (
                  <article className='landing-briefing-card landing-reveal' style={{ '--landing-delay': `${index * 80}ms` }} key={briefing.country}>
                    <div className='landing-briefing-flag'>
                      <img
                        src={getFlagUrl(briefing.flag, 80)}
                        alt={`${briefing.country} flag`}
                        onError={(event) => {
                          event.currentTarget.style.display = 'none'
                          event.currentTarget.nextSibling.style.display = 'inline-flex'
                        }}
                      />
                      <span className='landing-country-flag-fallback' style={{ display: 'none' }}>{getFlagFallback(briefing.flag)}</span>
                    </div>
                    <div className='landing-briefing-body'>
                      <div className='landing-briefing-top'>
                        <span>{briefing.country}</span>
                      </div>
                      <p>{briefing.summary}</p>
                    </div>
                    {briefing.pdfUrl && briefing.isPublished ? (
                      <a className='landing-briefing-pdf' href={briefing.pdfUrl} target='_blank' rel='noreferrer'>
                        <LandingIcon name='pdf' size={16} />
                        Open {briefing.period}
                      </a>
                    ) : (
                      <span className='landing-briefing-status'>Coming soon</span>
                    )}
                  </article>
                ))}
              </div>

              <div className='landing-briefings-footer'>
                <button className='landing-api-action landing-api-action-primary' type='button' onClick={() => navigate('/country-briefings')}>View All Country Briefings</button>
              </div>
            </div>
          </section>
        )}

        <section className='landing-account-section' id='landing-dashboard'>
          <div className='container'>
            <div className='landing-section-header centered landing-reveal'>
              <h2 className='landing-section-title'>Keep the capabilities users already have.</h2>
              <p className='landing-section-description'>The redesigned landing still routes users into account creation, saved workspaces, and dashboard-oriented flows.</p>
            </div>

            <div className='landing-account-grid'>
              {ACCOUNT_FEATURES.map((feature, index) => (
                <div className='landing-account-card landing-reveal' style={{ '--landing-delay': `${index * 110}ms` }} key={feature.title}>
                  <div className='landing-account-icon'><LandingIcon name={feature.icon} /></div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>

            {!userInfo && (
              <div className='landing-account-cta'>
                <button className='landing-account-cta-button' onClick={() => openModal('register')}>Create Free Account</button>
              </div>
            )}
          </div>
        </section>

        <section className='landing-api-section' id='landing-api'>
          <div className='container landing-api-grid'>
            <div className='landing-api-copy landing-reveal'>
              <h2 className='landing-section-title'>Explore the dataset through the IDB catalog and API.</h2>
              <p className='landing-section-description'>LMW remains connected to the public IDB data infrastructure. The landing keeps the documentation and endpoint workflow visible for more technical users.</p>
              <ul className='landing-api-list'>
                <li><LandingIcon name='check' size={17} /><span>Filter and query the public dataset directly.</span></li>
                <li><LandingIcon name='check' size={17} /><span>Move from landing to search without losing context.</span></li>
                <li><LandingIcon name='check' size={17} /><span>Copy the endpoint and continue in code or dashboards.</span></li>
              </ul>
              <div className='landing-api-actions'>
                <a className='landing-api-action landing-api-action-primary' href='https://data.iadb.org/dataset/latin-macro-watch-dataset' target='_blank' rel='noreferrer'>Access Dataset</a>
                <a className='landing-api-action landing-api-action-secondary' href='https://dev.socrata.com/foundry/mydata.iadb.org/tbnd-z94f' target='_blank' rel='noreferrer'>API Documentation</a>
              </div>
              <div className='landing-api-endpoint'>
                <span>API endpoint</span>
                <button type='button' onClick={() => copyText('landing-api-endpoint')}>Copy</button>
              </div>
              <div className='landing-api-endpoint-link' id='landing-api-endpoint'>https://data.iadb.org/dataset/latin-macro-watch-dataset</div>
            </div>

            <div className='landing-api-code landing-reveal' style={{ '--landing-delay': '140ms' }}>
              <pre>
                <span className='landing-code-punctuation'>{'{'}</span>
                {'\n'}
                {API_CODE_SAMPLE.map((line, index) => (
                  <React.Fragment key={line.key}>
                    <span className='landing-code-indent'>  </span>
                    <span className='landing-code-key'>"{line.key}"</span>
                    <span className='landing-code-punctuation'>: </span>
                    <span className={`landing-code-value landing-code-value-${line.type}`}>{line.value}</span>
                    {index < API_CODE_SAMPLE.length - 1 ? <span className='landing-code-punctuation'>,</span> : null}
                    {'\n'}
                  </React.Fragment>
                ))}
                <span className='landing-code-punctuation'>{'}'}</span>
              </pre>
            </div>
          </div>
        </section>

      </main>

      <LandingFooter />
      {authModals}
    </div>
  )
}

export default MainScreen
